import { ChartValueAxis, chartValueTicks } from '../lib/ChartValueAxis.tsx';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, Clock, Minus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChartAxisLabels } from '../lib/ChartAxisLabels.tsx';
import { Link } from 'react-router';
import { Field, Input, Select, cn } from '@crm/ui';
import { formatDate, formatDateTime } from '../lib/format.ts';
import { formatMoney } from '../lib/money.ts';
import { useTenant } from '../tenant/TenantProvider.tsx';
import {
  ANA_PATH,
  axisBounds,
  COMPARES,
  deltaDirection,
  formatDeltaPct,
  groupMissingRates,
  monthLabel,
  periodLabel,
  PRESETS,
  type ChartModel,
  type Compare,
  type Grain,
  type MissingRate,
  type Preset,
} from './data.ts';

export function AnalyticsForbidden({ reason = 'read' }: { reason?: 'read' | 'group' | 'config' }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-600" data-testid="analytics-forbidden">
      {t(`analytics.forbidden.${reason}`)}
    </div>
  );
}

/** « Données au 24 oct. 2026 à 10:00 » : heure de la dernière actualisation réussie (contrat analyse § 1.4). */
export function Freshness({ at, testId = 'analytics-freshness' }: { at: string | null | undefined; testId?: string }) {
  const { t, i18n } = useTranslation();
  return (
    <p className="inline-flex items-center gap-1 text-xs text-slate-600" data-testid={testId}>
      <Clock size={12} aria-hidden />
      {at ? t('analytics.freshness', { at: formatDateTime(at, i18n.language) }) : t('analytics.neverRefreshed')}
    </p>
  );
}

/** Avertissement : montants exclus faute de taux (jamais convertis à 1), par devise et par mois ; lien vers les taux. */
export function MissingRatesNotice({ missing, reporting, excluded, testId = 'analytics-missing-rates' }: { missing: MissingRate[]; reporting: string | null; excluded?: boolean; testId?: string }) {
  const { t, i18n } = useTranslation();
  const { can } = useTenant();
  if (!missing.length && !excluded) return null;
  const groups = groupMissingRates(missing);
  return (
    <div role="status" className="space-y-1 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid={testId}>
      <p className="flex items-start gap-2 font-semibold">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
        {t('analytics.missingRates.title', { currency: reporting ?? '—' })}
      </p>
      {groups.length ? (
        <ul className="list-disc space-y-0.5 pl-8">
          {groups.map((g) => (
            <li key={g.currency} data-testid="missing-rate">
              <span className="font-mono font-semibold">{g.currency}</span> : {g.months.map((m) => monthLabel(m, i18n.language)).join(', ')}
            </li>
          ))}
        </ul>
      ) : null}
      <p className="pl-6">
        {t('analytics.missingRates.hint')}{' '}
        {can('analytics.read') ? (
          <Link to={ANA_PATH.rates} className="font-medium underline">{t('analytics.missingRates.link')}</Link>
        ) : null}
      </p>
    </div>
  );
}

/** « 1 200,00 EUR ; 50,00 USD » : montants exclus faute de taux, une entrée par devise d'origine. */
export function excludedText(list: { currency: string; amount: string }[], loc: string): string {
  return list.map((x) => formatMoney(x.amount, loc, x.currency)).join(' ; ');
}

/** Repère d'un montant exclu faute de taux (icône + texte pour les lecteurs d'écran) ; rien si la liste est vide. */
export function ExcludedMark({ list, inline = true }: { list: { currency: string; amount: string }[] | null | undefined; inline?: boolean }) {
  const { t, i18n } = useTranslation();
  if (!list?.length) return null;
  const text = t('analytics.excluded', { amounts: excludedText(list, i18n.language) });
  if (!inline) return <span className="block whitespace-normal text-xs text-amber-800" data-testid="ana-excluded">{text}</span>;
  return (
    <span className="ml-1 inline-flex items-center text-amber-700" title={text} data-testid="ana-excluded">
      <AlertTriangle size={12} aria-hidden />
      <span className="sr-only">{text}</span>
    </span>
  );
}

/** Écart en % avec son sens écrit (icône + signe ; jamais la couleur seule). */
export function DeltaPct({ value, testId }: { value: string | null | undefined; testId?: string }) {
  const { t, i18n } = useTranslation();
  const dir = deltaDirection(value);
  if (!dir) return <span className="text-slate-400" data-testid={testId}>—</span>;
  const Icon = dir === 'up' ? ArrowUpRight : dir === 'down' ? ArrowDownRight : Minus;
  return (
    <span className={cn('inline-flex items-center gap-0.5 whitespace-nowrap tabular-nums', dir === 'up' ? 'text-emerald-800' : dir === 'down' ? 'text-red-800' : 'text-slate-600')} data-testid={testId} data-direction={dir}>
      <Icon size={12} aria-hidden />
      <span className="sr-only">{t(`analytics.direction.${dir}`)} </span>
      {formatDeltaPct(value, i18n.language)}
    </span>
  );
}

/** Période : raccourcis, dates libres (raccourci « personnalisée ») et comparaison. */
export function PeriodControls({
  preset,
  from,
  to,
  range,
  compare,
  onChange,
  testId = 'ana',
}: {
  preset: Preset;
  from: string;
  to: string;
  range: { from: string; to: string };
  compare: Compare;
  onChange: (patch: { preset?: Preset; from?: string; to?: string; compare?: Compare }) => void;
  testId?: string;
}) {
  const { t, i18n } = useTranslation();
  return (
    <div className={cn('grid gap-3 sm:grid-cols-2', preset === 'perso' && 'lg:grid-cols-4')}>
      <Field label={t('analytics.fields.period')} hint={preset !== 'perso' ? t('analytics.rangeHint', { from: formatDate(range.from, i18n.language), to: formatDate(range.to, i18n.language) }) : undefined}>
        {(p) => (
          <Select
            {...p}
            value={preset}
            onChange={(e) => {
              const next = e.target.value as Preset;
              // Passer en « personnalisée » part des dates du raccourci affiché.
              onChange(next === 'perso' ? { preset: next, from: range.from, to: range.to } : { preset: next });
            }}
            data-testid={`${testId}-preset`}
          >
            {PRESETS.map((x) => <option key={x} value={x}>{t(`analytics.preset.${x}`)}</option>)}
          </Select>
        )}
      </Field>
      {preset === 'perso' ? (
        <>
          <Field label={t('analytics.fields.from')}>
            {(p) => <Input {...p} type="date" value={from} max={to || undefined} onChange={(e) => e.target.value && onChange({ from: e.target.value })} data-testid={`${testId}-from`} />}
          </Field>
          <Field label={t('analytics.fields.to')}>
            {(p) => <Input {...p} type="date" value={to} min={from || undefined} onChange={(e) => e.target.value && onChange({ to: e.target.value })} data-testid={`${testId}-to`} />}
          </Field>
        </>
      ) : null}
      <Field label={t('analytics.fields.compare')}>
        {(p) => (
          <Select {...p} value={compare} onChange={(e) => onChange({ compare: e.target.value as Compare })} data-testid={`${testId}-compare`}>
            {COMPARES.map((c) => <option key={c} value={c}>{t(`analytics.compare.${c}`)}</option>)}
          </Select>
        )}
      </Field>
    </div>
  );
}

/** Teintes catégorielles dans un ordre fixe (identité d'une série ; jamais selon le rang). */
export const SERIES_COLORS = ['#2563a9', '#c45225', '#047857', '#a16207', '#a33d69', '#166534'];
const OTHER_COLOR = '#64748b';

/**
 * Graphique simple en SVG (courbes par tranche de temps, ou barres par ligne), un seul axe. Il est décoratif pour les
 * lecteurs d'écran : le tableau du résultat, toujours affiché dessous, porte les mêmes valeurs. Survol d'une tranche ou
 * d'une barre : ses valeurs ; légende écrite pour plusieurs séries (jamais la couleur seule).
 */
export function AnalyticsChart({ model, grain, format, title, testId = 'analytics-chart' }: { model: ChartModel; grain: Grain | null; format: (raw: string | null) => string; title: string; testId?: string }) {
  const { t, i18n } = useTranslation();
  const [active, setActive] = useState<number | null>(null);
  const [hovering, setHovering] = useState(false);
  const [hidden, setHidden] = useState<string[]>([]);
  const W = 600;
  const H = 220;
  const pad = { l: 4, r: 6, t: 12, b: 12 };
  const visible = model.series.map((s, index) => ({ series: s, index })).filter(({ series }) => !hidden.includes(series.key));
  const hasVisibleValues = visible.some(({ series }) => series.points.some((point) => point !== null));
  // La sélection de la légende adapte aussi l'échelle aux courbes visibles.
  const { min, max } = axisBounds(visible.map(({ series }) => series));
  const y = (v: number) => pad.t + ((max - v) / (max - min)) * (H - pad.t - pad.b);
  const n = Math.max(model.categories.length, 1);
  const step = (W - pad.l - pad.r) / n;
  const x = (i: number) => pad.l + step * i + step / 2;
  const catLabel = (c: string) => (model.kind === 'line' ? periodLabel(c, grain, i18n.language, t('analytics.weekOf')) : c);
  const color = (i: number, other: boolean) => (other ? OTHER_COLOR : SERIES_COLORS[i % SERIES_COLORS.length]!);
  const multi = model.series.length > 1;
  const selected = Math.max(0, Math.min(active ?? model.categories.length - 1, model.categories.length - 1));
  const axis = chartValueTicks(min, max, i18n.language);
  if (!model.categories.length) return null;
  return (
    <figure className="@container min-w-0 space-y-3" data-testid={testId}>
      <figcaption className="text-sm font-medium text-slate-700">
        {title}
        <span className="sr-only"> — {t('analytics.chart.srNote')}</span>
      </figcaption>
      {multi ? (
        <ul className="flex flex-wrap gap-2 text-xs text-slate-700" data-testid={`${testId}-legend`}>
          {model.series.map((s, i) => (
            <li key={s.key}>
              <button type="button" aria-pressed={!hidden.includes(s.key)} onClick={() => setHidden((keys) => keys.includes(s.key) ? keys.filter((key) => key !== s.key) : [...keys, s.key])} className={cn('inline-flex min-h-9 items-center gap-2 rounded-full border px-3 py-1 font-medium transition-colors', hidden.includes(s.key) ? 'border-slate-200 text-slate-500 line-through' : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100')}>
                <span className="inline-block h-1 w-4 rounded" style={{ backgroundColor: color(i, s.isOther) }} aria-hidden />{s.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {visible.length > 0 && !hasVisibleValues && <p className="rounded-xl bg-slate-50 px-4 py-8 text-center text-sm text-slate-600">{t('cockpits.empty')}</p>}
      {hasVisibleValues && <div className="relative min-w-0 rounded-2xl border border-slate-100 bg-gradient-to-b from-slate-50/80 to-white px-3 pb-3 pt-4 sm:px-4" aria-hidden>
        <div className="grid gap-2" style={{ gridTemplateColumns: `${axis.width}px minmax(0, 1fr)` }}>
        <ChartValueAxis ticks={axis.ticks} top={pad.t} bottom={pad.b} height={H} />
        <div className="min-w-0"><svg viewBox={`0 0 ${W} ${H}`} className="h-56 w-full" preserveAspectRatio="none" role="presentation">
          {[0, 0.25, 0.5, 0.75, 1].map((f) => <line key={f} x1={pad.l} x2={W - pad.r} y1={pad.t + f * (H - pad.t - pad.b)} y2={pad.t + f * (H - pad.t - pad.b)} className="stroke-slate-200/80" strokeWidth="1" vectorEffect="non-scaling-stroke" />)}
          <line x1={pad.l} x2={W - pad.r} y1={y(0)} y2={y(0)} className="stroke-slate-300" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          {model.kind === 'bars'
            ? model.series[0]!.points.map((v, i) => {
                const top = v === null ? y(0) : Math.min(y(v), y(0));
                const h = v === null ? 0 : Math.abs(y(v) - y(0));
                return (
                  <g key={i} onMouseEnter={() => { setActive(i); setHovering(true); }} onMouseLeave={() => setHovering(false)}>
                    <rect x={pad.l + step * i} y={0} width={step} height={H} fill="transparent" />
                    <rect x={pad.l + step * i + step * 0.15} y={top} width={step * 0.7} height={h} rx="3" fill={SERIES_COLORS[0]} opacity={active !== null && selected !== i ? 0.5 : 1} data-testid={`${testId}-bar`} />
                  </g>
                );
              })
            : (
              <>
                {visible.map(({ series: s, index: si }) => {
                  const segments: string[] = [];
                  let current = '';
                  s.points.forEach((v, i) => {
                    if (v === null) {
                      if (current) segments.push(current);
                      current = '';
                    } else current += `${current ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
                  });
                  if (current) segments.push(current);
                  return (
                    <g key={s.key} data-testid={`${testId}-line`}>
                      {segments.map((d, k) => <path key={k} d={d} fill="none" stroke={color(si, s.isOther)} strokeWidth="2.75" strokeDasharray={s.isOther ? '4 3' : si % 3 === 1 ? '7 4' : si % 3 === 2 ? '2 4' : undefined} vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />)}
                      {s.points.map((v, i) => {
                        const isolated = (i === 0 || s.points[i - 1] === null) && (i === s.points.length - 1 || s.points[i + 1] === null);
                        return v === null || (s.points.length > 1 && selected !== i && !isolated) ? null : <circle key={i} cx={x(i)} cy={y(v)} r="4" fill={color(si, s.isOther)} stroke="#fff" strokeWidth="2" vectorEffect="non-scaling-stroke" />;
                      })}
                    </g>
                  );
                })}
                {model.categories.map((_, i) => (
                  <rect key={i} x={pad.l + step * i} y={0} width={step} height={H} fill="transparent" onMouseEnter={() => { setActive(i); setHovering(true); }} onMouseLeave={() => setHovering(false)} />
                ))}
                {active !== null ? <line x1={x(selected)} x2={x(selected)} y1={pad.t} y2={H - pad.b} className="stroke-slate-400" strokeWidth="1" vectorEffect="non-scaling-stroke" pointerEvents="none" /> : null}
              </>
            )}
        </svg>
        <ChartAxisLabels labels={model.categories.map(catLabel)} position={(index) => x(index) / W * 100} />
        </div></div>
        {hovering && active !== null ? (
          <div className="pointer-events-none absolute left-1/2 top-0 max-w-[90%] -translate-x-1/2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow" data-testid={`${testId}-tooltip`}>
            <p className="font-semibold">{catLabel(model.categories[selected]!)}</p>
            {visible.map(({ series: s, index: si }) => (
              <p key={s.key} className="flex items-center gap-1 tabular-nums">
                {multi ? <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: color(si, s.isOther) }} /> : null}
                {multi ? `${s.label} : ` : ''}
                {format(s.raw[selected] ?? null)}
              </p>
            ))}
          </div>
        ) : null}
      </div>}
      {visible.length ? <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-3 text-xs" data-testid={`${testId}-detail`}>
        <strong className="text-slate-700">{catLabel(model.categories[selected]!)}</strong>
        {visible.map(({ series: s }) => <span key={s.key} className="text-slate-600">{multi ? `${s.label} : ` : ''}<strong className="tabular-nums text-slate-950">{format(s.raw[selected] ?? null)}</strong></span>)}
      </div> : <p className="text-sm text-slate-600">{t('visuals.selectSeries')}</p>}
      {model.categories.length > 1 && <label className="flex items-center gap-3 text-xs text-slate-600"><span>{t(model.kind === 'line' ? 'visuals.explore' : 'visuals.exploreCategories')}</span><input type="range" min="0" max={model.categories.length - 1} value={selected} onChange={(event) => setActive(Number(event.target.value))} aria-label={t(model.kind === 'line' ? 'visuals.explore' : 'visuals.exploreCategories')} aria-valuetext={catLabel(model.categories[selected]!)} className="h-6 min-w-0 flex-1 accent-[var(--color-brand)]" /></label>}
      {model.hidden > 0 ? <p className="text-xs text-slate-500" data-testid={`${testId}-hidden`}>{t(model.kind === 'line' ? 'analytics.chart.hiddenSeries' : 'analytics.chart.hiddenBars', { count: model.hidden })}</p> : null}
    </figure>
  );
}
